import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Truck, Package, Printer, RefreshCw, ExternalLink, Loader2, MapPin, Phone, Mail, RotateCcw, Ban, CheckCircle2, FileText, Clock } from "lucide-react";
import { api, inr, fileUrl, openPdf, errorMessage } from "@/lib/api";
import { Pagination, usePaged } from "@/components/Pagination";

export const ORDER_STATUSES = ["awaiting_payment", "payment_failed", "pending", "confirmed", "packed", "shipped", "delivered", "cancelled", "returned"];
const label = (s) => (s || "").replace(/_/g, " ");
const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };
const STATUS_COLOR = {
  awaiting_payment: "bg-[#FFF7E0] text-[#8a6d00]", payment_failed: "bg-[#FDECEC] text-[#991B1B]", pending: "bg-neutral-100 text-neutral-700",
  confirmed: "bg-[#E8F3EE] text-[#0E4D34]", packed: "bg-[#E8EEF8] text-[#1D4E89]", shipped: "bg-[#E8EEF8] text-[#1D4E89]",
  delivered: "bg-[#0E4D34] text-white", cancelled: "bg-[#FDECEC] text-[#991B1B]", returned: "bg-neutral-200 text-neutral-700",
};
export function StatusPill({ status }) {
  return <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-1 whitespace-nowrap ${STATUS_COLOR[status] || "bg-neutral-100"}`}>{label(status)}</span>;
}

export default function Orders() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["adm-orders"], queryFn: () => api.get("/admin/orders").then(r => r.data) });
  const [q, setQ] = React.useState("");
  const [st, setSt] = React.useState("");
  const [open, setOpen] = React.useState(null);
  const list = (data || []).filter(o => (!st || o.status === st) &&
    (!q || [o.order_number, o.customer_name, o.customer_email, (o.shipping_info || {}).awb].some(v => (v || "").toLowerCase().includes(q.toLowerCase()))));
  const pg = usePaged(list, 20, { resetKey: `${q}|${st}` });
  const update = async (id, status) => {
    try { await api.patch(`/admin/orders/${id}`, { status }); refetch(); qc.invalidateQueries({ queryKey: ["adm-order", id] }); toast.success("Status updated"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const counts = (data || []).reduce((m, o) => ({ ...m, [o.status]: (m[o.status] || 0) + 1 }), {});
  return (
    <div>
      <div className="flex justify-between items-end gap-3 flex-wrap mb-5">
        <div>
          <h1 className="h2-editorial">Orders</h1>
          <p className="text-sm text-neutral-500 mt-1">{(data || []).length} orders · click an order to see details, ship it with Shiprocket, or handle returns</p>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Order no., name, email, AWB" className="border border-neutral-300 p-2.5 text-sm w-full sm:w-72"/>
      </div>
      <div className="flex gap-2 flex-wrap mb-4" data-testid="orders-status-filter">
        <button onClick={() => setSt("")} className={`text-xs px-3 py-1.5 border rounded-full ${!st ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "bg-white"}`}>All ({(data || []).length})</button>
        {ORDER_STATUSES.filter(s => counts[s]).map(s => (
          <button key={s} onClick={() => setSt(s)} className={`text-xs px-3 py-1.5 border rounded-full capitalize ${st === s ? "bg-[#0E4D34] text-white border-[#0E4D34]" : "bg-white"}`}>{label(s)} ({counts[s]})</button>
        ))}
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-widest"><tr>{["Order", "Customer", "Total", "Payment", "Shipping", "Status", ""].map(h => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-neutral-500">No orders.</td></tr>}
            {pg.items.map(o => {
              const sh = o.shipping_info || {};
              return (
                <tr key={o.id} className="border-t hover:bg-neutral-50 cursor-pointer" onClick={() => setOpen(o.id)} data-testid={`order-row-${o.order_number}`}>
                  <td className="p-3"><div className="font-mono font-semibold">{o.order_number}</div><div className="text-[11px] text-neutral-500">{fmt(o.created_at)}</div></td>
                  <td className="p-3">{o.customer_name}<br/><span className="text-xs text-neutral-500">{(o.address || {}).city}</span></td>
                  <td className="p-3 font-semibold">{inr(o.total)}</td>
                  <td className="p-3 text-xs"><span className="uppercase">{o.payment_method}</span><br/><span className={o.payment_status === "paid" ? "text-[#0E4D34] font-semibold" : ""}>{label(o.payment_status)}</span></td>
                  <td className="p-3 text-xs">{sh.awb ? <><div className="font-semibold">{sh.courier_name}</div><div className="font-mono text-neutral-500">{sh.awb}</div></> : sh.shipment_id ? "In Shiprocket" : o.tracking_number ? <span className="font-mono">{o.tracking_number}</span> : <span className="text-neutral-400">—</span>}
                    {o.return_status && <div className="mt-1 text-[10px] uppercase tracking-widest text-[#991B1B]">Return: {label(o.return_status)}</div>}</td>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <select value={o.status} onChange={e => update(o.id, e.target.value)} data-testid={`status-${o.order_number}`} className="border p-1 text-xs">
                      {[...new Set([o.status, ...ORDER_STATUSES.filter(s => !s.includes("payment"))])].map(s => <option key={s} value={s}>{label(s)}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right"><span className="text-xs text-[#0E4D34] underline">Open</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination {...pg.props} label="orders"/>
      {open && <OrderDrawer id={open} onClose={() => setOpen(null)} onChanged={refetch}/>}
    </div>
  );
}

export function Drawer({ title, subtitle, onClose, children, testid }) {
  React.useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative w-full max-w-2xl h-full bg-[#FAF9F6] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-4 flex items-start justify-between gap-3">
          <div><h2 className="font-semibold text-lg">{title}</h2>{subtitle && <div className="text-xs text-neutral-500 mt-0.5">{subtitle}</div>}</div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 hover:bg-neutral-100 rounded-full"><X size={18}/></button>
        </div>
        <div className="p-4 sm:p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

export function Card({ title, icon: Icon, children, right }) {
  return (
    <div className="bg-white border p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-xs uppercase tracking-widest text-neutral-600 flex items-center gap-2">{Icon && <Icon size={14} className="text-[#0E4D34]"/>}{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function OrderDrawer({ id, onClose, onChanged }) {
  const qc = useQueryClient();
  const { data: o, refetch } = useQuery({ queryKey: ["adm-order", id], queryFn: () => api.get(`/orders/${id}`).then(r => r.data) });
  const { data: returns } = useQuery({ queryKey: ["adm-order-returns", id], queryFn: () => api.get("/admin/returns").then(r => r.data.filter(x => x.order_id === id)) });
  const refresh = () => { refetch(); onChanged?.(); qc.invalidateQueries({ queryKey: ["adm-returns"] }); };
  if (!o) return <Drawer title="Order" onClose={onClose}><div className="p-6 text-sm text-neutral-500">Loading…</div></Drawer>;
  const a = o.address || {};
  return (
    <Drawer title={`Order ${o.order_number}`} subtitle={fmt(o.created_at)} onClose={onClose} testid="order-drawer">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill status={o.status}/>
        <span className="text-xs text-neutral-600">Payment: <b className="uppercase">{o.payment_method}</b> · {label(o.payment_status)}</span>
        {o.refunded_total > 0 && <span className="text-xs text-[#991B1B]">Refunded {inr(o.refunded_total)}</span>}
        <button type="button" onClick={() => openPdf(`/orders/${o.id}/invoice`).catch(e => toast.error(errorMessage(e)))} className="ml-auto text-xs border px-3 py-1.5 bg-white flex items-center gap-1"><FileText size={12}/>Invoice</button>
      </div>

      <Card title="Items" icon={Package}>
        {o.items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
            {it.image ? <img src={fileUrl(it.image)} alt="" className="w-10 h-12 object-cover rounded"/> : <span className="w-10 h-12 bg-neutral-100 rounded"/>}
            <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{it.name}</div><div className="text-xs text-neutral-500">{inr(it.price)} × {it.quantity}</div></div>
            <div className="text-sm">{inr(it.price * it.quantity)}</div>
          </div>
        ))}
        <div className="text-sm pt-3 space-y-1">
          {o.discount > 0 && <div className="flex justify-between text-neutral-600"><span>Discount</span><span>−{inr(o.discount)}</span></div>}
          <div className="flex justify-between text-neutral-600"><span>GST</span><span>{inr(o.tax)}</span></div>
          <div className="flex justify-between text-neutral-600"><span>Shipping</span><span>{o.shipping ? inr(o.shipping) : "Free"}</span></div>
          <div className="flex justify-between font-bold text-[#0E4D34]"><span>Total</span><span>{inr(o.total)}</span></div>
        </div>
      </Card>

      <Card title="Customer & address" icon={MapPin}>
        <div className="text-sm leading-relaxed">
          <div className="font-semibold">{a.full_name}</div>
          <div>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
          <div>{a.city}, {a.state} {a.pincode}</div>
          <div className="flex gap-4 mt-2 text-xs flex-wrap"><a href={`tel:${a.phone}`} className="flex items-center gap-1 text-[#0E4D34]"><Phone size={12}/>{a.phone}</a><a href={`mailto:${o.customer_email}`} className="flex items-center gap-1 text-[#0E4D34]"><Mail size={12}/>{o.customer_email}</a></div>
        </div>
      </Card>

      <ShippingPanel order={o} onChanged={refresh}/>

      <Card title="Returns" icon={RotateCcw} right={<Link to="/admin/returns" className="text-xs text-[#0E4D34] underline">All returns</Link>}>
        {(returns || []).length === 0 ? <p className="text-sm text-neutral-500">No return requests for this order.</p> : (
          <div className="space-y-2">{returns.map(r => (
            <Link key={r.id} to={`/admin/returns?open=${r.id}`} className="flex items-center justify-between border p-2.5 text-sm hover:border-[#0E4D34]">
              <span className="font-mono">{r.return_number}</span><span className="text-xs">{r.items.map(i => `${i.name} ×${i.quantity}`).join(", ")}</span><StatusPill status={r.status}/>
            </Link>))}
          </div>)}
        {o.status === "delivered" && <CreateReturnInline order={o} onDone={refresh}/>}
      </Card>

      {(o.history || []).length > 0 && (
        <Card title="Order history" icon={Clock}>
          <ol className="space-y-2 text-sm">{[...o.history].reverse().map((h, i) => (
            <li key={i} className="flex gap-3"><span className="text-xs text-neutral-500 w-36 shrink-0">{fmt(h.at)}</span><span><b className="capitalize">{label(h.status)}</b>{h.note ? ` — ${h.note}` : ""} <span className="text-xs text-neutral-400">({h.by})</span></span></li>))}
          </ol>
        </Card>
      )}
    </Drawer>
  );
}

function CreateReturnInline({ order, onDone }) {
  const [open, setOpen] = React.useState(false);
  if (!open) return <button onClick={() => setOpen(true)} className="mt-3 text-xs border px-3 py-1.5 hover:border-[#0E4D34]" data-testid="admin-create-return">Create a return for the customer</button>;
  return <div className="mt-3 border-t pt-3"><ReturnForm order={order} staff onDone={() => { setOpen(false); onDone(); }} onCancel={() => setOpen(false)}/></div>;
}

/** Return request form — used by customers (My Account) and by staff (admin order drawer). */
export function ReturnForm({ order, staff, onDone, onCancel }) {
  const { data } = useQuery({ queryKey: ["returnable", order.id], queryFn: () => api.get(`/orders/${order.id}/returnable`).then(r => r.data) });
  const [qty, setQty] = React.useState({});
  const [reason, setReason] = React.useState("");
  const [comments, setComments] = React.useState("");
  const [upi, setUpi] = React.useState("");
  const [resolution, setResolution] = React.useState("refund");
  const [busy, setBusy] = React.useState(false);
  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
  if (!data.eligible && !staff) return <div className="text-sm text-neutral-600 bg-neutral-50 border p-3">{data.reason}</div>;
  const submit = async (e) => {
    e.preventDefault();
    const items = Object.entries(qty).filter(([, n]) => n > 0).map(([product_id, quantity]) => ({ product_id, quantity }));
    if (!items.length) return toast.error("Choose at least one item");
    if (!reason) return toast.error("Choose a reason");
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/returns`, { items, reason, comments, resolution, refund_upi: upi || null });
      toast.success("Return request sent");
      onDone?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const cod = order.payment_method === "cod";
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="return-form">
      {!data.eligible && staff && <div className="text-xs bg-[#FFF7E0] p-2">Note: {data.reason} (staff can still create it)</div>}
      <div className="text-xs uppercase tracking-widest text-neutral-600">Items to return</div>
      {data.items.map(it => (
        <div key={it.product_id} className={`flex items-center gap-3 border p-2.5 ${it.returnable_qty ? "" : "opacity-50"}`}>
          {it.image ? <img src={fileUrl(it.image)} alt="" className="w-10 h-12 object-cover rounded"/> : null}
          <div className="flex-1 min-w-0 text-sm"><div className="truncate font-medium">{it.name}</div><div className="text-xs text-neutral-500">{it.note || `Can return ${it.returnable_qty} of ${it.quantity}`}</div></div>
          <select disabled={!it.returnable_qty} value={qty[it.product_id] || 0} onChange={e => setQty({ ...qty, [it.product_id]: Number(e.target.value) })}
                  className="border p-1.5 text-sm" data-testid={`return-qty-${it.product_id}`}>
            {Array.from({ length: it.returnable_qty + 1 }).map((_, n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      ))}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1">Reason</label>
          <select value={reason} onChange={e => setReason(e.target.value)} className="w-full border p-2.5 text-sm bg-white" data-testid="return-reason">
            <option value="">Choose…</option>{data.reasons.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1">I would like</label>
          <select value={resolution} onChange={e => setResolution(e.target.value)} className="w-full border p-2.5 text-sm bg-white">
            <option value="refund">A refund</option><option value="exchange">An exchange</option>
          </select>
        </div>
      </div>
      <textarea value={comments} onChange={e => setComments(e.target.value)} rows={3} maxLength={1000} placeholder="Tell us more (optional) — e.g. where the damage is" className="w-full border p-2.5 text-sm"/>
      {cod && resolution === "refund" && (
        <input value={upi} onChange={e => setUpi(e.target.value)} placeholder="UPI ID for the refund (you paid cash on delivery)" className="w-full border p-2.5 text-sm" data-testid="return-upi"/>
      )}
      <div className="text-[11px] text-neutral-500">Returns are accepted within {data.window_days} days of delivery. Pieces must be unused, with tags. Our courier will pick the parcel up from your address.</div>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-silk rounded-[10px] disabled:opacity-50" data-testid="return-submit">{busy ? "Sending…" : "Request return"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="btn-outline-silk rounded-[10px]">Cancel</button>}
      </div>
    </form>
  );
}

function ShippingPanel({ order, onChanged }) {
  const { data: cfg } = useQuery({ queryKey: ["ship-cfg"], queryFn: () => api.get("/admin/shipping/settings").then(r => r.data), staleTime: 60000 });
  const sh = order.shipping_info || {};
  const [busy, setBusy] = React.useState("");
  const [pkg, setPkg] = React.useState({ weight: "", length: "", breadth: "", height: "" });
  const [couriers, setCouriers] = React.useState(null);
  const [courier, setCourier] = React.useState("");
  const [manual, setManual] = React.useState({ carrier: order.carrier || "", tracking_number: order.tracking_number || "", tracking_url: order.tracking_url || "" });
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); onChanged(); return r; }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(""); }
  };
  const blocked = ["cancelled", "returned", "awaiting_payment", "payment_failed"].includes(order.status);
  const B = ({ k, children, onClick, primary, testid }) => (
    <button type="button" onClick={onClick} disabled={!!busy} data-testid={testid}
            className={`text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-50 ${primary ? "bg-[#0E4D34] text-white hover:bg-[#0A3926]" : "border bg-white hover:border-[#0E4D34]"}`}>
      {busy === k ? <Loader2 size={12} className="animate-spin"/> : null}{children}
    </button>
  );

  return (
    <Card title="Shipping" icon={Truck} right={cfg && <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${cfg.configured ? "bg-[#E8F3EE] text-[#0E4D34]" : "bg-[#FFF7E0] text-[#8a6d00]"}`}>{cfg.configured ? "Shiprocket connected" : "Shiprocket not set up"}</span>}>
      {/* Already in Shiprocket */}
      {sh.shipment_id ? (
        <div className="space-y-3 text-sm" data-testid="shipment-box">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-neutral-500">Shiprocket order</span><div className="font-mono">{sh.sr_order_id}</div></div>
            <div><span className="text-neutral-500">Shipment</span><div className="font-mono">{sh.shipment_id}</div></div>
            <div><span className="text-neutral-500">Courier</span><div className="font-semibold">{sh.courier_name || "—"}</div></div>
            <div><span className="text-neutral-500">AWB / tracking no.</span><div className="font-mono">{sh.awb || "—"}</div></div>
            <div><span className="text-neutral-500">Status</span><div className="font-semibold">{sh.sr_status || "—"}</div></div>
            <div><span className="text-neutral-500">Parcel</span><div>{sh.weight} kg · {sh.length}×{sh.breadth}×{sh.height} cm</div></div>
            {sh.pickup_date && <div><span className="text-neutral-500">Pickup</span><div>{String(sh.pickup_date)}</div></div>}
            {sh.etd && <div><span className="text-neutral-500">Expected delivery</span><div>{String(sh.etd)}</div></div>}
          </div>
          {!sh.awb && (
            <div className="border-t pt-3 space-y-2">
              <div className="text-xs text-neutral-600"><b>Step 2:</b> choose a courier (price & delivery time from Shiprocket), then book it. Leave it on "Recommended" if unsure.</div>
              <div className="flex gap-2 flex-wrap items-center">
                <B k="couriers" testid="load-couriers" onClick={async () => { const r = await run("couriers", () => api.get(`/admin/orders/${order.id}/couriers`)); if (r) setCouriers(r.data.couriers); }}>Show couriers & prices</B>
                {couriers && (
                  <select value={courier} onChange={e => setCourier(e.target.value)} className="border p-2 text-xs max-w-full" data-testid="courier-select">
                    <option value="">Recommended by Shiprocket</option>
                    {couriers.map(c => <option key={c.id} value={c.id}>{c.recommended ? "★ " : ""}{c.name} — ₹{c.rate} · {c.days ? `${c.days} days` : c.etd}</option>)}
                  </select>
                )}
              </div>
              {couriers && couriers.length === 0 && <div className="text-xs text-[#991B1B]">No courier delivers to this pincode for this parcel.</div>}
              <B k="ship" primary testid="book-courier" onClick={() => run("ship", () => api.post(`/admin/orders/${order.id}/ship`, { courier_id: courier ? Number(courier) : null }), "Courier booked and pickup requested")}>
                <Truck size={12}/>Book courier & schedule pickup</B>
            </div>
          )}
          {sh.awb && !sh.pickup_scheduled && (
            <B k="ship" primary onClick={() => run("ship", () => api.post(`/admin/orders/${order.id}/ship`, {}), "Pickup requested")}>Schedule pickup</B>
          )}
          <div className="flex gap-2 flex-wrap border-t pt-3">
            {sh.awb && <B k="label" testid="print-label" onClick={async () => { const r = await run("label", () => api.get(`/admin/orders/${order.id}/label`)); if (r?.data?.label_url) window.open(r.data.label_url, "_blank"); }}><Printer size={12}/>Shipping label</B>}
            {sh.awb && <B k="track" onClick={() => run("track", () => api.post(`/admin/orders/${order.id}/track`), "Tracking updated")}><RefreshCw size={12}/>Refresh tracking</B>}
            {sh.awb && <a href={order.tracking_url || `https://shiprocket.co/tracking/${sh.awb}`} target="_blank" rel="noreferrer" className="text-xs border px-3 py-2 bg-white flex items-center gap-1.5 hover:border-[#0E4D34]"><ExternalLink size={12}/>Tracking page</a>}
            {!["delivered", "shipped"].includes(order.status) && (
              <B k="cancel" onClick={() => window.confirm("Cancel this shipment in Shiprocket? You can create it again afterwards.") && run("cancel", () => api.post(`/admin/orders/${order.id}/shipment/cancel`), "Shipment cancelled")}><Ban size={12}/>Cancel shipment</B>
            )}
          </div>
          {(sh.events || []).length > 0 && <Timeline events={sh.events}/>}
        </div>
      ) : blocked ? (
        <p className="text-sm text-neutral-500">This order can't be shipped while it is "{label(order.status)}".</p>
      ) : cfg?.configured ? (
        <div className="space-y-3">
          <div className="text-xs text-neutral-600"><b>Step 1:</b> check the parcel size, then send the order to Shiprocket (pickup from <b>{cfg.pickup_location}</b>).</div>
          <div className="grid grid-cols-4 gap-2">
            {[["weight", "Weight kg"], ["length", "L cm"], ["breadth", "B cm"], ["height", "H cm"]].map(([k, l]) => (
              <div key={k}><label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">{l}</label>
                <input type="number" step="0.1" min="0" value={pkg[k]} onChange={e => setPkg({ ...pkg, [k]: e.target.value })}
                       placeholder={String(k === "weight" ? Math.max(0.2, (cfg.weight_per_item || 0.6) * order.items.reduce((n, i) => n + i.quantity, 0)).toFixed(1) : cfg[k])}
                       className="w-full border p-2 text-sm"/></div>
            ))}
          </div>
          <B k="create" primary testid="send-to-shiprocket" onClick={() => run("create", () => api.post(`/admin/orders/${order.id}/shipment`, Object.fromEntries(Object.entries(pkg).filter(([, v]) => v).map(([k, v]) => [k, Number(v)]))), "Order sent to Shiprocket")}>
            <Truck size={12}/>Send to Shiprocket</B>
        </div>
      ) : (
        <p className="text-sm text-neutral-600">Shiprocket isn't connected yet (see Admin → Settings → Shipping). You can still ship with any courier and enter the tracking details below.</p>
      )}

      {/* Manual courier details — for orders not shipped through Shiprocket */}
      {!sh.shipment_id && !blocked && (
        <details className="mt-4 border-t pt-3">
          <summary className="text-xs text-[#0E4D34] cursor-pointer">Shipped another way? Enter courier & tracking number</summary>
          <div className="grid sm:grid-cols-3 gap-2 mt-3">
            <input value={manual.carrier} onChange={e => setManual({ ...manual, carrier: e.target.value })} placeholder="Courier (e.g. DTDC)" className="border p-2 text-sm"/>
            <input value={manual.tracking_number} onChange={e => setManual({ ...manual, tracking_number: e.target.value })} placeholder="Tracking number" className="border p-2 text-sm"/>
            <input value={manual.tracking_url} onChange={e => setManual({ ...manual, tracking_url: e.target.value })} placeholder="Tracking link (optional)" className="border p-2 text-sm"/>
          </div>
          <B k="manual" onClick={() => run("manual", () => api.patch(`/admin/orders/${order.id}`, { ...manual, status: ["pending", "confirmed", "packed"].includes(order.status) ? "shipped" : null }), "Saved — order marked shipped")}><CheckCircle2 size={12}/>Save & mark shipped</B>
        </details>
      )}
    </Card>
  );
}

export function Timeline({ events }) {
  return (
    <ol className="relative border-l-2 border-[#0E4D34]/20 ml-2 mt-2 space-y-3" data-testid="tracking-timeline">
      {events.map((e, i) => (
        <li key={i} className="ml-4">
          <span className={`absolute -left-[7px] mt-1 w-3 h-3 rounded-full ${i === 0 ? "bg-[#0E4D34]" : "bg-[#0E4D34]/30"}`}/>
          <div className="text-sm font-medium">{e.status || e.activity}</div>
          <div className="text-xs text-neutral-500">{[e.activity && e.activity !== e.status ? e.activity : null, e.location, e.at].filter(Boolean).join(" · ")}</div>
        </li>
      ))}
    </ol>
  );
}

/** Admin → Settings → Shipping (Shiprocket) */
export function ShippingSettings() {
  const { data, refetch } = useQuery({ queryKey: ["ship-cfg"], queryFn: () => api.get("/admin/shipping/settings").then(r => r.data) });
  const [f, setF] = React.useState(null);
  React.useEffect(() => { if (data && !f) setF(data); }, [data, f]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const keys = ["pickup_location", "warehouse_name", "warehouse_address", "warehouse_city", "warehouse_state", "warehouse_pincode", "warehouse_phone", "warehouse_email", "weight_per_item", "length", "breadth", "height", "return_window_days"];
      const body = Object.fromEntries(keys.map(k => [k, ["weight_per_item", "length", "breadth", "height", "return_window_days"].includes(k) ? Number(f[k]) : f[k]]));
      await api.put("/admin/shipping/settings", body); toast.success("Shipping settings saved"); refetch();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const In = ({ k, l, type = "text", span }) => (
    <div className={span ? "sm:col-span-2" : ""}><label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">{l}</label>
      <input type={type} value={f[k] ?? ""} onChange={set(k)} className="w-full border border-neutral-300 p-2.5 text-sm"/></div>
  );
  return (
    <div className="bg-white border p-6 space-y-4" data-testid="shipping-settings">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold flex items-center gap-2"><Truck size={16}/>Shipping (Shiprocket) & returns</h3>
        <span className={`text-xs px-2.5 py-1 ${data.configured ? "bg-[#E8F3EE] text-[#0E4D34]" : "bg-[#FFF7E0] text-[#8a6d00]"}`}>{data.configured ? "Connected" : "Not connected — add SHIPROCKET_EMAIL / SHIPROCKET_PASSWORD to backend/.env"}</span>
      </div>
      {data.error && <div className="text-xs bg-[#FDECEC] text-[#991B1B] p-2.5">{data.error}</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">Pickup location (name in Shiprocket)</label>
          {data.pickup_locations?.length ? (
            <select value={f.pickup_location} onChange={set("pickup_location")} className="w-full border border-neutral-300 p-2.5 text-sm bg-white">
              {[...new Set([f.pickup_location, ...data.pickup_locations])].map(p => <option key={p}>{p}</option>)}
            </select>
          ) : <input value={f.pickup_location || ""} onChange={set("pickup_location")} className="w-full border border-neutral-300 p-2.5 text-sm"/>}
        </div>
        {In({ k: "return_window_days", l: "Return window (days after delivery)", type: "number" })}
        {In({ k: "warehouse_name", l: "Store / warehouse name (returns come here)" })}
        {In({ k: "warehouse_phone", l: "Store phone" })}
        {In({ k: "warehouse_address", l: "Store address", span: true })}
        {In({ k: "warehouse_city", l: "City" })}
        {In({ k: "warehouse_state", l: "State" })}
        {In({ k: "warehouse_pincode", l: "Pincode" })}
        {In({ k: "warehouse_email", l: "Store email" })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {In({ k: "weight_per_item", l: "Weight per saree (kg)", type: "number" })}
        {In({ k: "length", l: "Box length (cm)", type: "number" })}
        {In({ k: "breadth", l: "Box breadth (cm)", type: "number" })}
        {In({ k: "height", l: "Box height (cm)", type: "number" })}
      </div>
      <div className="text-xs text-neutral-500 bg-neutral-50 border p-3 leading-relaxed">
        Tracking webhook (optional, recommended): in Shiprocket → Settings → API → Webhooks, set the URL to
        <code className="mx-1 bg-white border px-1">{`${window.location.origin}${data.webhook_path}`}</code>
        and the token to the value of <code className="bg-white border px-1">SHIPROCKET_WEBHOOK_TOKEN</code> in backend/.env
        {data.webhook_token_set ? " (set ✓)." : " (not set yet)."} Then order statuses update by themselves (picked up → shipped → delivered).
      </div>
      <button onClick={save} className="btn-silk" data-testid="save-shipping">Save shipping settings</button>
    </div>
  );
}
